<script setup>
import Vue, {computed, onMounted, reactive, ref, watch, inject, nextTick} from "vue";
import {Avatar} from "vue-avatar/src";
import {statuses} from "@/constants/needs";
import needsDashboardService from "@/services/needs-dashboard.service";
import needsItemService from "@/services/needs-item.service";

const {type} = defineProps(['type']);

const needsTypes = inject('needsTypes');
const archivedAnchorClicked = inject('archivedAnchorClicked');
const needs = ref([]);
const search = ref('');
const usersList = ref([]);

const filters = reactive({
    search: '',
    status: '',
    userId: null,
});

const escalated = computed(() => type !== 'non-escalated');

watch(filters, () => {
    getList();
});

onMounted(() => {
    getUsersList();
    getList();
});

async function getList() {
    const {data} = await needsDashboardService.getNeedsList(type, filters);
    needs.value = data;

    await nextTick();
    if (archivedAnchorClicked.value && type === 'archived') {
        document.getElementById(`needs-archived`).scrollIntoView({behavior: 'smooth'});
    }
}

async function getUsersList() {
    const {data} = await needsDashboardService.getNeedsUsersList(type);
    usersList.value = data;
}

async function nudge(need) {
    if (!confirm(`Nudge ${need.nudge}?`)) return;
    const {data} = await needsItemService.nudge(need.id);

    if (data) {
        Vue.notify({
            group: 'app',
            type: 'success',
            duration: 5000,
            text: "Message sent"
        });

        needs.value.find(item => item.id === need.id).nudge = null;
    } else {
        Vue.notify({
            group: 'app',
            type: 'error',
            duration: 5000,
            text: "Error occurred"
        })
    }
}
</script>

<template>
    <div :id="`needs-${type}`" class="need-admin__card need-admin__card--round">
        <div class="need-admin__card__inner">
            <header class="need-admin__card__header">
                <h3
                    class="need-admin__title"
                    :class="{'need-admin__title--escalated': type === 'escalated', 'need-admin__title--progress': type === 'non-escalated', 'need-admin__title--archived': type === 'archived'}"
                >
                    {{needsTypes[type]}}
                </h3>
            </header>

            <div class="need-admin__filter">
                <select v-if="escalated" v-model="filters.status" class="need-admin__select">
                    <option value="">Status</option>
                    <option v-for="status in statuses" :key="status" :value="status">{{status}}</option>
                </select>

                <select class="need-admin__select" v-model="filters.userId">
                    <option :value="null">User</option>
                    <option v-for="user in usersList" :key="user.id" :value="user.id">{{user.name}}</option>
                </select>

                <div class="ml-auto">
                    <input
                        type="text"
                        v-model="search"
                        placeholder="Search for a need"
                        class="need-admin__search"
                        v-debounce:500="() => filters.search = search"
                    />
                </div>
            </div>
        </div>

        <div v-if="needs.length" class="need-admin__table">
            <table>
                <thead>
                <tr>
                    <th>Need</th>
                    <th>Owner</th>
                    <th v-if="escalated">Status</th>
                    <th v-if="!escalated">Recommended</th>
                    <th>Added From AI</th>
                    <th>Added From Ecosystems</th>
                    <th v-if="!escalated">Rated</th>
                    <th>&nbsp;</th>
                </tr>
                </thead>

                <tbody>
                <template
                    v-for="need in needs"
                >
                    <tr :key="need.id">
                        <td>
                            <h6 class="heading6">{{need.name}}</h6>
                            <template v-if="escalated">
                                <p v-if="need.status === 'converted'">Converted {{need.status_date}}</p>
                                <p v-else-if="need.status === 'rejected'">Rejected {{need.status_date}}</p>
                                <p v-else :class="{'danger': need.escalated_old}">Escalated {{need.escalated_date}}</p>
                            </template>

                            <p v-else>Created {{need.created_date}}</p>
                        </td>
                        <td>
                            <span v-if="need.user" class="need-admin__user">
                                <Avatar :src="need.user.avatar" :username="need.user.name" :size="34" class="need-admin__user__image" :title="need.user.name"/>
                            </span>
                        </td>
                        <td v-if="escalated">
                            <span class="need-admin__status-plate" :class="`need-admin__status-plate--${need.status}`">{{statuses[need.status]}}</span>
                        </td>
                        <td v-if="!escalated">
                            <span v-if="need.recommended_count > 0" class="evaluated-stat">{{ need.recommended_count }}</span>
                        </td>
                        <td>
                            <span v-if="need.from_ai_count > 0" class="evaluated-stat">{{ need.from_ai_count }}</span>
                        </td>
                        <td>
                            <span v-if="need.from_ecosystems_count > 0" class="evaluated-stat">{{ need.from_ecosystems_count }}</span>
                        </td>
                        <td v-if="!escalated" class="text-center" style="padding-right: 24px;">
                            {{ need.rated_count }}
                        </td>
                        <td>
                            <a :href="`/needs/${need.id}`" style="text-decoration: none" :class="escalated ? 'need-admin__review' : 'need-admin__view'" target="_blank">{{ escalated ? 'Review' : 'View' }}</a>
                        </td>
                    </tr>

                    <tr v-if="need.nudge" :key="`nudge-${need.id}`">
                        <td colspan="7">
                            <div class="need-admin__card__color need-admin__card__color--obsolete">
                                This need hasn't been updated in over a week

                                <a @click="nudge(need)" class="link-arrow">Nudge {{need.nudge}}</a>
                            </div>
                        </td>
                    </tr>
                </template>

                <!--            <tr>-->
                <!--                <td colspan="6">-->
                <!--                    <div class="need-admin__table__footer">-->
                <!--                        Find more results with AI powered search (premium)-->
                <!--                        <a href="#" class="link">Find More Startups (AI-Powered)</a>-->
                <!--                    </div>-->
                <!--                </td>-->
                <!--            </tr>-->
                </tbody>
            </table>
        </div>

        <div v-else class="text-center">
            Needs not found
        </div>
    </div>
</template>

<style scoped>

</style>
